import { create } from 'zustand';
import { craftService } from '../services/craftService';
import { type CraftSchema } from '@/schemas/craft/create';
import { templateService } from '@/services/templateService';
import { legalsService, type LegalItem } from '@/services/legalsService';
import { normalizePopulateForm, normalizeTaxonomies, normalizeTemplates } from '@/normalize/normalizeCraft';


interface CraftOption {
  id: string;
  name: string;
  description: string;
}

interface CraftAction extends CraftOption { }
interface CraftFormat extends CraftOption { }
export interface CraftTemplate {
  id: string;
  image: string;
  name: string;
}
export interface CraftTaxonomySpecification {
  text: string;
  color: string | null;
}

export interface CraftTaxonomy {
  id: string;
  name: string;
  format_type: string;
  layout: string[];
  preview_image_url: string | null;
  specifications: Record<string, CraftTaxonomySpecification | null>;
}

export interface CraftData {
  actions?: CraftAction[];
  formats?: CraftFormat[];
  taxonomies?: CraftTaxonomy[];
  templates?: CraftTemplate[];
  legals?: LegalItem[];
}

interface CraftPagination {
  current: number;
  size: number;
  total: number;
  formatType: string;
}

interface CraftState {
  data: CraftData | null;
  pagination: CraftPagination;
  saveForm: (craft: CraftSchema) => Promise<{ success: boolean, message: string, id?: number, isCombo?: boolean, process_image_id?: number }>;
  populateForm: () => Promise<void>;
  getTaxonomies: (formatType: string) => Promise<void>;
  getTemplates: (params?: { page?: number, formatType?: string }) => Promise<any>;
  getLegals: () => Promise<void>;
  resetFormatType: () => void;
}

export const useCraftStore = create<CraftState>((set, get) => ({
  data: null,
  pagination: {
    current: 1,
    size: 14,
    total: 1,
    formatType: ''
  },
  populateForm: async () => {
    try {
      const populateResult = await craftService.populateForm();
      const data = normalizePopulateForm(populateResult);
      set({ data });
    } catch (error) {
      console.error('Error populating craft:', error);
    }
  },
  saveForm: async (craft: CraftSchema) => {
    try {
      const result = await craftService.saveForm(craft);

      if (!result.job_id) {
        return {
          success: false,
          message: 'Error al iniciar el proceso',
        };
      }
      
      // Query queued file
      let statusResult = await craftService.getStatusBulkGenerate(result.job_id);
      while (!statusResult.process_image_id) {
        await new Promise(resolve => setTimeout(resolve, 5000));
        statusResult = await craftService.getStatusBulkGenerate(result.job_id);
      }
      
      // Catch error
      if (statusResult.status === 'error') {
        return {
          success: false,
          message: 'Error al generar el combo',
        };
      }
      
      return {
        ...result, 
        process_image_id: statusResult.process_image_id
      };
    } catch (error) {
      return { success: false, message: 'Error al guardar manualidad' };
    }
  },
  getTaxonomies: async (formatType: string) => {
    try {
      const result = await craftService.getTaxonomies(formatType);
      const taxonomies = normalizeTaxonomies(result);
      set({ data: { ...get().data, taxonomies } });
    } catch (error) {
      console.error('Error getting taxonomies:', error);
    }
  },
  getTemplates: async (params?: { page?: number, formatType?: string }) => {
    try {
      const fetchParams = {
        page: params?.page || get().pagination.current,
        page_size: get().pagination.size,
        format_type: params?.formatType || get().pagination.formatType
      }
      const filteredParams = Object.fromEntries(
        Object.entries(fetchParams).filter(([, v]) => v !== '' && v !== undefined && v !== null)
      );
      const response = await templateService.getTemplates(filteredParams);
      const templates = normalizeTemplates(response);
      set({
        data: {
          ...get().data,
          templates
        },
        pagination: {
          ...get().pagination,
          total: response.count,
          formatType: params?.formatType || get().pagination.formatType
        }
      });
    } catch (error) {
      console.error('Error getting templates:', error);
    }
  },
  getLegals: async () => {
    try {
      const response = await legalsService.getLegalsList({ is_active: true });
      set({ data: { ...get().data, legals: response.results } });
    } catch (error) {
      console.error('Error getting legals:', error);
    }
  },
  resetFormatType: () => {
    set({ pagination: { ...get().pagination, formatType: '' } });
  }
}));
